import CMSWrapper from '@/features/shared/CMSWrapper';
import ContentContainer from '@/features/shared/ContentContainer';
import { MuiContainer } from '@/features/shared/material-ui';
import {
  resolveBlacklistUrls,
  resolveColorName,
  resolveCRMSyncs,
  resolveRTC,
  resolveSpacingSize,
  resolveTheme,
} from '@/utils/resolvers';
import React, { useMemo, useRef } from 'react';
import MarketoForm from './MarketoForm';

const MappedForm = ({ content, presentation, contentButtons, pageModels }: any) => {
  const { topSpacing, bottomSpacing, containerBackgroundColor, isFullBleed } = presentation;
  const containerProps = useMemo(
    () => ({
      topSpacing: !isFullBleed && topSpacing,
      bottomSpacing: !isFullBleed && bottomSpacing,
      topBackgroundColor: containerBackgroundColor,
      bottomBackgroundColor: containerBackgroundColor,
    }),
    [bottomSpacing, containerBackgroundColor, isFullBleed, topSpacing],
  );
  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: containerBackgroundColor }),
    [containerBackgroundColor],
  );
  const { config, labels } = pageModels || {};
  const { marketo: marketoConfig } = config || {};
  const { marketo: marketoLabels } = labels || {};

  return (
    <ContentContainer
      {...containerProps}
      datalayerPosition="marketo_form"
      datalayerVariant={presentation?.datalayerVariant}
    >
      <MuiContainer
        maxWidth={isFullBleed ? false : undefined}
        className={!isFullBleed ? 'medium' : ''}
      >
        <MarketoForm
          {...content}
          theme={theme}
          containerColor={containerBackgroundColor}
          contentButtons={contentButtons}
          recaptchaSitekey={marketoConfig?.recaptcha_sitekey}
          recaptchaLegal={marketoConfig?.recaptcha_legal}
          emailFieldname={marketoConfig?.email_fieldname}
          emailBlacklistErrorMessage={marketoConfig?.email_blacklist_error_message}
          emailCRMFieldname={marketoConfig?.email_crm_fieldname}
          labels={marketoLabels}
        />
      </MuiContainer>
    </ContentContainer>
  );
};

const resolveTemplate = ({ rawVal }: any) => rawVal && rawVal === 'full-bleed';

const MarketoFormCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        marketoFormId: 'formId',
        confirmationMessage: [null, resolveRTC],
        rules: 'rules',
        heading: 'heading',
        description: ['formDescription', resolveRTC],
        emailBlacklistUrls: ['emailsBlacklisted', resolveBlacklistUrls],
        crmSyncs: ['CRMSyncFieldnames', resolveCRMSyncs],
      },
    ],
    componentParameterMap: [
      'presentation',
      {
        template: ['isFullBleed', resolveTemplate],
        'container-background-color': ['containerBackgroundColor', resolveColorName],
        'top-spacing': ['topSpacing', resolveSpacingSize],
        'bottom-spacing': ['bottomSpacing', resolveSpacingSize],
        'variant-label': 'datalayerVariant',
      },
    ],
  });

  const requiredFields = useRef({
    document: '<b>Content</b> required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedForm}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default MarketoFormCMSWrapper;
